/*
 * HOW A TURN LOOKS — one renderer, every surface that shows one.
 *
 * A bubble carrying what the turn thought, what it ran, and what it said is not the chat view's
 * private business: the transcript detail view shows it, a sub-agent's thread shows it, and a node
 * opened out of the run hierarchy shows it. It lived inside `<vitesse-ai-chat>` because that was the
 * only surface at the time, and a second copy would have been two designs drifting apart the first
 * time either learned about a new part type — which is precisely the visual-consistency failure this
 * codebase keeps removing by making the shared thing the only thing.
 *
 * FUNCTIONS AND A STYLESHEET, not a component. A turn is a fragment inside someone else's scroll
 * container; making it an element would put a shadow boundary between the bubble and the layout that
 * positions it, and would hand every caller a second thing to style around. Callers `@include` the
 * styles into their own `static styles` and call the render functions from their template.
 *
 * DISCLOSURE STATE STAYS WITH THE CALLER. Which thinking panels and which tool rows are open is the
 * VIEW's business — the same conversation replayed into a second surface must not inherit the
 * first's open panels — so it is passed in rather than held here. That also keeps this module pure:
 * no state, no IO, no lifecycle.
 */
import { html, nothing, css } from 'lit';
import { renderMarkdown } from './markdown';
import { renderJson } from './json-view';
import { renderWorkCard, workCardFor, renderWorkList } from './work-card';
import { t } from '../i18n';
import { isWaitTool, toolDisplayName } from './toolname';
import { askRow } from './ask-row';
import { spawnedRunIdOfTool, rootRunIdOfTool } from './spawn-ids';
import { parseReturn, splitStepReply, TURN_RUN_PREFIX } from '@vitesse/agent-loop';
import '../elements/vitesse-icon';
import './artifact-view';
/** The turn stylesheet. Include it wherever `renderTurn` is called — the markup is meaningless
 *  without it, and a caller that forgets gets unstyled text rather than a subtle drift. */
/**
 * How long a disclosure takes, in ms — the ONE definition.
 *
 * The transcript has to follow the scroll for exactly as long as the card is still changing height,
 * so the number is shared rather than written twice. Two copies of a duration drift the moment one
 * of them is tuned, and the symptom is a scroll that stops following slightly too early.
 */
/**
 * A TRANSCRIPT THAT IS ON ITS WAY, drawn as the thing it is about to be.
 *
 * Two surfaces show it — the chat while its archive lands, and the panel while an agent's thread is
 * fetched — and they are the same moment: content is coming, and the honest picture is its shape
 * rather than a sentence about the wait. It is exported as a template rather than copied because
 * the second caller was, until recently, rendering the words "This agent has not started yet"
 * instead, about agents that had finished twenty minutes earlier.
 *
 * Three bars, one short. Enough to read as prose without pretending to know how much is coming.
 */
export function transcriptSkeleton() {
    return html `<div class="skeleton" aria-busy="true" aria-label=${t('ai.opening')}>
    ${[0, 1, 2].map(() => html `<div class="sk-row"><span class="sk-bar"></span><span class="sk-bar short"></span></div>`)}
  </div>`;
}
export const FOLD_MS = 240;
export const turnStyles = css `
  /*
   * ONE CURVE FOR EVERY DISCLOSURE in a turn — the thinking row, a tool card, the payload inside it.
   *
   * Ease-in-out: slow, faster, slow, and SYMMETRIC, so closing is the opening played backwards. An
   * asymmetric curve is what makes a disclosure feel like it snaps shut after opening gently, and
   * it is the thing people notice without being able to name.
   */
  :host {
    --fold-dur: ${FOLD_MS}ms;
    --fold-ease: cubic-bezier(0.65, 0, 0.35, 1);
  }
  /*
   * TWO SHAPES, ONE FOR EACH SPEAKER, and the asymmetry is the point.
   *
   * The ASSISTANT gets no container at all: its answers are the substance of the thread, they are
   * long, and they carry work (thinking, tool rows, subtrees) that reads as structure rather than as
   * speech. Boxing that made a wall of chrome around the thing people came to read, and capped it at
   * a fraction of a rail that is already narrow.
   *
   * The USER gets the container, because a question needs to be findable when you scroll back
   * through a long thread — it is the landmark, not the content. It takes the surface the assistant
   * used to sit on rather than a saturated accent: a rail is read for minutes at a time, and a block
   * of white-on-blue every few hundred pixels is the thing that makes that tiring.
   *
   * So: the speaker is distinguished by SHAPE (boxed vs open) rather than by colour, which survives
   * both themes and does not shout.
   */
  /* A column, so the actions sit UNDER the message rather than beside it, and inherit its edge:
     right-aligned under a question, left-aligned under an answer. */
  .turn {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    /*
     * Room for the action row to float INTO — see .turn-acts, and keep the arithmetic true:
     *
     *   row height = padding-top (4) + button (18) + padding-bottom (0) = 22px
     *   this margin  = 26px  ⇒  4px of clearance before the next turn begins
     *
     * It was 22px of margin under a 34px row, so the icons were drawn ON TOP of the next message —
     * which is exactly where the reader's own words are, and the one place they must not be.
     */
    position: relative;
    margin-bottom: 26px;
  }
  .turn.mine {
    align-items: flex-end;
  }
  .bubble {
    /* Generous, because a narrow rail is mostly margin otherwise — 88% still reads as "a block that
       does not span the column", which is all the indent has to say.
       fit-content is what stops a three-word question drawing a box the width of the rail: the box
       is sized by its text and only capped by the max, so the padding hugs the message instead of
       framing empty space beside it. */
    width: fit-content;
    max-width: 88%;
    padding: 5px 9px;
    border-radius: 10px;
    font-size: 14px;
    line-height: 1.5;
    /* NORMAL, not pre-wrap — see the note on .open. */
    white-space: normal;
    word-break: break-word;
    background: var(--bg-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
  }
  /* The assistant's side: the full column, no box, no border. Padding only where prose against a
     panel edge would otherwise touch it. */
  .open {
    width: 100%;
    padding: 2px 2px 2px 0;
    font-size: 14px;
    line-height: 1.55;
    /*
     * NORMAL, and this is the fix for "too much space between the thought and the message".
     *
     * It was pre-wrap, from when a reply was a raw string that had to keep its newlines. It is
     * markdown now, and the block elements carry the line structure — so all pre-wrap still did was
     * render the RENDERERS' OWN template whitespace: the newline and indent that renderThinking
     * and renderTool open and close with became a blank line above and below each of them. That
     * is where the empty band between the thinking row, the tool row and the answer came from. Not
     * margins, which were already ~2px; literal text.
     */
    white-space: normal;
    word-break: break-word;
    color: var(--text-primary);
  }
  /*
   * The action row: present in the DOM always, visible on hover or when something inside it has
   * focus. Opacity rather than display, so it is reachable by keyboard and does not reflow the turn
   * when it appears — a row that changed the layout on hover would nudge the text under the pointer.
   */
  /*
   * THE ACTIONS FLOAT; THEY DO NOT OCCUPY A ROW.
   *
   * They were laid out in flow at opacity 0, which meant every turn — including one nobody was
   * hovering — reserved a full row of invisible buttons. On a thread of short messages that
   * invisible row was taller than the messages, and it read as the transcript being loosely spaced
   * when in fact it was mostly empty controls.
   *
   * Absolute, so the row costs nothing until it is wanted, and positioned into the gap BELOW the
   * turn rather than over its last line — so revealing it still moves nothing (the no-unpurposeful-
   * layout-shift rule holds) and covers nothing.
   */
  .turn-acts {
    position: absolute;
    top: 100%;
    left: 0;
    display: flex;
    gap: 2px;
    /* A shade below the message rather than tucked against it — close enough to belong to that
       turn, far enough not to read as its last line. */
    margin-top: 0;
    /* A REACHABLE TARGET, not a precise one.
       
       The row sits outside the turn's box, so travelling to it crosses ground that belongs to the
       next turn — and it was vanishing mid-reach. Two changes, both about the pointer rather than
       the pixels: the row keeps a padded hit area on all sides (so a near miss still lands), and it
       fades out on a DELAY, so passing over a few pixels of neighbouring turn does not dismiss the
       thing being aimed at. Appearing stays instant; only leaving waits. */
    padding: 4px 8px 0 4px;
    margin-left: -4px;
    opacity: 0;
    transition: opacity 0.12s ease 0.28s;
    pointer-events: none;
  }
  .turn.mine .turn-acts {
    left: auto;
    right: 0;
  }
  .turn:hover .turn-acts,
  .turn:focus-within .turn-acts {
    opacity: 1;
    pointer-events: auto;
    /* No delay on the way IN — the row should be there the moment the turn is. */
    transition-delay: 0s;
  }
  /*
   * THE VERSION SWITCHER — ‹ 2/3 › on the turn a branch was taken at.
   *
   * Sits in the action row rather than in its own strip: it belongs to the same message the branch
   * came off, and a second row would double the chrome under every branched turn. It appears on
   * hover with everything else — it was pinned open on the theory that a fact about where you are
   * outranks a thing you can do, and in practice that just put a permanent control under one turn
   * in a transcript where every other turn's chrome is quiet until you reach for it.
   *
   * The arrows carry the same grammar as the icon buttons beside them: flat, blue on hover, grown a
   * touch, dimmed when there is nowhere to go.
   */
  .ver {
    display: inline-flex;
    align-items: center;
    gap: 1px;
    margin-right: 4px;
    color: var(--text-tertiary);
    font-size: 11px;
    font-variant-numeric: tabular-nums;
  }
  .ver-arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 18px;
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    cursor: pointer;
    transition:
      color 0.12s ease,
      transform 0.12s ease;
  }
  .ver-arrow:hover:not(:disabled) {
    color: var(--ai);
    transform: scale(1.12);
  }
  .ver-arrow:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .ver-n {
    padding: 0 1px;
  }
  /* The platform's icon-button behaviour at a smaller size: flat, grows on hover, blue on hover.
     Same grammar as every other icon button, one step down, because these annotate a message rather
     than acting on the page. */
  .turn-act {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* 18, not 20 — the glyph is 13px, so the box is hit area, and every pixel of it is charged to
       the gap between turns (see .turn's margin). */
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--text-tertiary);
    cursor: pointer;
    border-radius: 4px;
    transition:
      transform 0.12s ease,
      color 0.12s ease;
  }
  .turn-act:hover {
    color: var(--blue);
    transform: scale(1.07);
  }
  .turn-act:focus-visible {
    outline: 1px solid var(--blue);
    outline-offset: 1px;
  }
  /* The timestamp in the action row — muted, tabular, and out of the way of the icons. */
  .turn-stamp {
    font-size: 11px;
    color: var(--text-tertiary);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    align-self: center;
    padding: 0 2px;
  }
  /* A quarter-turn clockwise: upright the glyph reads as two paths merging upward, and this action
     is a branch leaving this point forward. */
  .turn-act.quarter vitesse-icon {
    transform: rotate(90deg);
  }
  .turn-err {
    color: var(--red);
    display: flex;
    align-items: flex-start;
    gap: 10px;
    flex-wrap: wrap;
  }
  .turn-err-msg {
    flex: 1 1 auto;
    min-width: 0;
  }
  /* Between the work and the answer, inside one bubble — present only when both exist. */
  /* The return reads as a margin note on the conversation: full width, no bubble, no speaker
     colour. It is neither side of the dialogue — it is what came back from somewhere else. */
  /*
   * The provenance note. Red because both findings it can carry are contradictions with the turn's
   * own tool calls rather than cautions — and quiet in weight, because it sits under an answer the
   * reader is already reading rather than replacing it.
   */
  /* Spelled out rather than shortened: .prov already means PROVIDER throughout this app — the model
     picker, the provider logo, the "Top provider" card — so the short name reads as the wrong thing
     in the one file where a misread is expensive. */
  /* The deliverable is rendered by renderJson — the SAME treatment a tool argument and a tool result
     already get in this file. A second monospace block styled here would be a third way to draw
     JSON in one transcript, and the difference would read as meaning. */
  /* Provenance, not a warning — quiet, and last. */
  .ret-sources {
    margin-top: 6px;
    font-size: 11.5px;
    line-height: 1.5;
    color: var(--text-secondary);
    overflow-wrap: anywhere;
  }
  /* Artifacts stack under the prose; one per line, because a chart is a thing to look at rather
     than a row of thumbnails to scan. */
  .artifacts {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 4px;
  }
  .provenance {
    margin-top: 8px;
    padding-left: 8px;
    border-left: 2px solid var(--red, #e03e42);
    font-size: 12px;
    color: var(--red, #e03e42);
  }
  .provenance-row {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    line-height: 1.5;
  }
  .provenance-row + .provenance-row {
    margin-top: 3px;
  }
  .ret {
    margin: 0 0 12px;
    padding-left: 8px;
    border-left: 2px solid var(--border-color);
    font-size: 12px;
  }
  .ret-head {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--text-tertiary);
  }
  .ret-body {
    margin-top: 2px;
    color: var(--text-secondary);
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }
  /*
   * MARKDOWN — see markdown.ts for why this is templates and not sanitised HTML.
   *
   * The block spacing is deliberately tighter than a document's: this is a chat turn, and a reply
   * with three short paragraphs should not be taller than the message list. :first-child/
   * :last-child collapse the outer margins so a rendered reply sits flush with the bubble.
   */
  /* The turn is pre-wrap so a plain reply keeps its newlines. Inside .md the block elements
     carry the line structure themselves, so pre-wrap here would render the RENDERER's own template
     whitespace as blank lines — the same trap documented at the message element below. */
  .md {
    white-space: normal;
  }
  .md-p,
  .md-h,
  .md-list,
  .md-quote,
  .md-fence {
    margin: 6px 0;
  }
  .md > :first-child {
    margin-top: 0;
  }
  .md > :last-child {
    margin-bottom: 0;
  }
  .md-h {
    font-weight: 650;
    line-height: 1.3;
  }
  .md-h1 {
    font-size: 1.22em;
  }
  .md-h2 {
    font-size: 1.12em;
  }
  .md-h3 {
    font-size: 1.04em;
  }
  .md-h4 {
    font-size: 1em;
    color: var(--text-secondary);
  }
  .md-list {
    padding-left: 20px;
  }
  .md-list li {
    margin: 2px 0;
  }
  /* Tables scroll inside their own box — a wide grid must never widen the rail. */
  .md-table-wrap {
    margin: 6px 0;
    overflow-x: auto;
  }
  .md-table {
    border-collapse: collapse;
    font-size: 12.5px;
    min-width: 60%;
  }
  .md-table th,
  .md-table td {
    padding: 4px 10px;
    border: 1px solid var(--border-color);
    text-align: left;
    white-space: nowrap;
  }
  .md-table th {
    font-weight: 600;
    background: color-mix(in srgb, var(--overlay-color) 5%, transparent);
  }
  .md-quote {
    padding-left: 9px;
    border-left: 2px solid var(--border-color);
    color: var(--text-secondary);
  }
  .md-hr {
    height: 1px;
    margin: 8px 0;
    background: var(--border-color);
  }
  .md-link {
    color: var(--ai);
  }
  .md-code {
    padding: 1px 4px;
    border-radius: 4px;
    background: var(--bg-tertiary);
    font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 0.92em;
  }
  /* A fenced block is a panel with a language strip, Confluence-style: the language is the label
     you look for when scanning a long reply, and the copy button is why anyone pastes SCXML or
     Python out of here at all. It scrolls on its own axis so one long line cannot widen the panel. */
  .md-fence {
    border: 1px solid var(--border-color);
    border-radius: 8px;
    overflow: hidden;
    background: var(--bg-secondary);
  }
  .md-fence-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 3px 8px;
    border-bottom: 1px solid var(--border-color);
    background: var(--bg-tertiary);
    font-size: 11px;
    color: var(--text-tertiary);
  }
  .md-lang {
    text-transform: lowercase;
    letter-spacing: 0.02em;
  }
  .md-copy {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--text-tertiary);
    cursor: pointer;
  }
  .md-copy:hover {
    color: var(--ai);
  }
  .md-pre {
    margin: 0;
    padding: 8px 10px;
    overflow-x: auto;
    font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 12px;
    line-height: 1.5;
    /* Overrides the turn's pre-wrap: code keeps its own lines and scrolls rather than reflowing. */
    white-space: pre;
  }
  /*
   * THE JSON PANELS — see json-view.ts for why this is a tokeniser and not an editor.
   *
   * Colours come from the app's own palette rather than an imported theme, so a tool result reads
   * like the rest of the product in both light and dark and needs no second theme to maintain.
   */
  /*
   * ONE CARD, SECTIONS — not two floating blocks.
   *
   * Arguments and Result are parts of the same call, so they are rows of the same panel, divided by
   * a rule and labelled in the margin rather than above. The old shape gave each its own heading and
   * its own bordered box, which is why two lines of JSON took a screen: most of the height was
   * chrome announcing which half you were looking at.
   */
  .j-wrap {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 5px 9px;
  }
  .j-wrap + .j-wrap {
    border-top: 1px solid var(--border-color);
  }
  /* One line, always. At 62px "ARGUMENTS" wrapped to "ARGUMEN / TS", which is the kind of detail
     that makes a panel look unfinished no matter what else is right about it. */
  .j-label {
    flex: none;
    width: 74px;
    padding-top: 1px;
    color: var(--text-tertiary);
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
  /* When the label IS the whole line — "No arguments" — it stops being a column. */
  .j-label.wide {
    width: auto;
  }
  .j-pre {
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 0;
    max-height: 260px;
    overflow: auto;
    border: 0;
    background: none;
    font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
    font-size: 11.5px;
    line-height: 1.45;
    white-space: pre;
  }
  .j-key {
    color: var(--ai);
  }
  .j-str {
    color: var(--green, #3fb950);
  }
  .j-num {
    color: var(--purple, #a371f7);
  }
  .j-lit {
    color: var(--yellow, #d29922);
  }
  .j-punct {
    color: var(--text-tertiary);
  }
  .j-plain {
    color: var(--text-secondary);
  }
  .stopped-note {
    display: block;
    margin-top: 4px;
    color: var(--text-tertiary);
    font-size: 11px;
    font-style: italic;
  }
  /* A caret while the reply arrives — the one honest signal that more is coming. */
  .caret {
    display: inline-block;
    width: 7px;
    color: var(--ai);
    animation: caret 1s step-end infinite;
  }
  @keyframes caret {
    0%,
    49% {
      opacity: 1;
    }
    50%,
    100% {
      opacity: 0;
    }
  }
  /*
   * THE THINKING DISCLOSURE — "Thinking" while it happens, "Thought for 4s" once it has.
   *
   * Collapsed by default, and that is the point rather than a default: reasoning is long,
   * repetitive and not the answer, so showing it inline would bury the reply the person asked
   * for. But it is never DISCARDED — a reasoning model's thinking is most of what you are
   * paying for and the only account of why it answered as it did, so it is always one click
   * away. This is why it is a disclosure and not a transient status.
   */
  /* Work rows sit INSIDE the bubble now, so they are laid out as lines of one block rather than
     as separate cards. The old spacing was drawn for standalone rows and left a turn looking
     like a stack of loosely related fragments. */
  /* Sealed: a label, not a control — no pointer, no highlight on hover. */
  .think-head.sealed,
  .think-head.sealed:hover .think-label {
    cursor: default;
    color: var(--text-secondary);
  }
  .think {
    font-size: 12px;
    /* A little air above and below — enough that the thought reads as its own beat before the
       answer rather than as the answer's first line, and not so much that a closed disclosure
       costs a paragraph of space. */
    margin: 1px 0 4px;
    line-height: 1.35;
  }
  .think + .tool,
  .tool + .tool {
    margin-top: 1px;
  }
  .think-head {
    display: flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
    font: inherit;
    color: var(--text-secondary);
    cursor: pointer;
    user-select: none;
  }
  /* Blue while it is the control you are operating — hovering it, or holding the disclosure open.
     The colour is what ties the label to the reasoning underneath it; grey-on-grey left the open
     and closed states looking identical apart from the chevron. */
  /* The chevron takes the highlight WITH the label — a tool row's does, and two disclosures a line
     apart behaving differently is the kind of thing that reads as unfinished. */
  .think-head:hover .think-label,
  .think-head:hover .think-chevron,
  .think-head[aria-expanded='true'] .think-label,
  .think-head[aria-expanded='true'] .think-chevron {
    color: var(--ai);
  }
  /* Rotates rather than swapping glyphs, so the control reads as one thing in two positions. */
  .think-chevron {
    display: inline-block;
    transition: transform 0.15s ease;
  }
  .think-head[aria-expanded='true'] .think-chevron {
    transform: rotate(90deg);
  }
  /* Live thinking shimmers; a finished thought does not. The animation IS the liveness signal —
     the label alone changes only once, at the end, and until then nothing on screen moves. */
  .think-head.live .think-label {
    background: linear-gradient(90deg, var(--text-secondary) 25%, var(--ai) 50%, var(--text-secondary) 75%) 0 0 / 300% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    animation: think-shimmer 2s linear infinite;
  }
  @keyframes think-shimmer {
    to {
      background-position: -300% 0;
    }
  }
  /* Italic, quieter than the answer, and scrollable rather than unbounded: reasoning runs to
     thousands of tokens, and a disclosure that pushes the reply off-screen when opened has made
     the transcript worse, not more transparent. The italics ARE the marker that this is thinking
     and not the reply — a left rule and a horizontal separator both said the same thing again in
     chrome, and cost vertical space to say it. */
  /*
   * THE SAME FOLD AS A TOOL CARD, for the same reason: one motion in the product, mirrored opening
   * and closing. It was rendered conditionally, so it appeared and vanished instantly next to a
   * card that animated — two disclosures on the same row behaving differently.
   */
  .think-fold {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    transition:
      grid-template-rows var(--fold-dur) var(--fold-ease),
      opacity var(--fold-dur) var(--fold-ease);
  }
  .think-fold.open {
    grid-template-rows: 1fr;
    opacity: 1;
  }
  .think-fold > .think-body {
    overflow: hidden;
    min-height: 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .think-fold {
      transition: none;
    }
  }
  .think-body {
    margin: 0 0 2px;
    font-style: italic;
    /*
     * Quieter than the answer AND quieter than a tool row — this is the one thing in a turn that is
     * not addressed to the reader. Tertiary rather than secondary, so scanning a thread skips it
     * unless you go looking.
     */
    color: var(--text-tertiary);
    line-height: 1.5;
    /* NOT pre-wrap: the body is rendered markdown now, so paragraphs and list items are real
       elements with their own spacing, and preserving the source's newlines on top of that spaces
       everything twice. The fenced blocks inside keep their own whitespace, as they always did. */
    word-break: break-word;
    max-height: 320px;
    overflow-y: auto;
  }
  /* While the thought is still arriving there is no cap: a capped box that does not scroll cut the
     live thinking off mid-sentence, and what it was about to say is the whole point of watching. */
  .think-head.live + .think-fold .think-body {
    max-height: none;
    overflow-y: visible;
  }
  /*
   * TOOL ROWS — what the turn actually DID, between the thinking and the answer.
   *
   * A row per call, in the order the model asked for it. Before this, a tool call was invisible:
   * the conversation tracked which tools were running and rendered it nowhere, so a turn that
   * read four symbols looked like a long unexplained pause followed by prose. On a platform
   * where a tool can write to plant, "what did it just do" is not a detail.
   *
   * Rows, not bubbles. They are not speech — nobody said them — and giving them the chat's
   * speech shape would claim the model told you something it did not.
   */
  /*
   * A TOOL CALL IS A CARD, the same shape the config page uses for an MCP server or a provider.
   *
   * It was a bare line with a tick, a left rule, and two labelled blocks of JSON floating under it —
   * three different treatments for one thing, and a left rule saying "this is nested" next to
   * content that was already obviously nested. Now it is one bordered card on the code-cell ground:
   * a header row you click, a separator, and the payload below. Consistent with the rest of the
   * product, and it reads as one object rather than as debris under a message.
   */
  .tool {
    font-size: 12px;
    /* The card owns the space around it, so whatever follows sits AGAINST its border rather than
       arriving before the collapse has finished giving the space back. */
    margin: 3px 0 5px;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: var(--bg-secondary);
    overflow: hidden;
  }
  /* The row is head + optional action; the head takes the space so the action sits hard right and
     the two never fight over the line. */
  /*
   * THE WORK CARD.
   *
   * Framed and full-width where a tool row is a line, because what it holds is the deliverable
   * rather than a note about one. Same border, radius and surface tokens as every other framed
   * thing in the product, so it reads as part of the transcript and not as an embed.
   */
  /* A question put to a person, inside its tool row's fold: the question, the options, the answer. */
  .ask-card {
    padding: 4px 0 8px;
    display: grid;
    gap: 8px;
  }
  .ask-card-q {
    font-size: 13.5px;
    color: var(--text-primary);
  }
  .ask-card-opts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .ask-card-opts span {
    font-size: 12px;
    padding: 2px 8px;
    border: 1px solid var(--border-color);
    border-radius: 999px;
    color: var(--text-secondary);
  }
  .ask-card-a {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
    color: var(--text-primary);
  }
  .ask-card-label {
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
    flex: 0 0 auto;
  }
  /* Still waiting reads as waiting — this is the state that makes a live run look stopped. */
  .ask-card-a.waiting .ask-card-label {
    color: var(--blue-light);
  }

  .work-card {
    border: 1px solid var(--border-color);
    border-radius: 10px;
    overflow: hidden;
    margin: 8px 0;
    background: var(--bg-secondary, transparent);
  }
  .work-card.error {
    border-color: var(--red, #e5484d);
  }
  .wc-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--border-color);
  }
  .wc-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* The badge sits with the title, not opposite it: a status pinned to the far wall of a wide card
     reads as belonging to the card rather than to the thing named. */
  .wc-state {
    margin-left: auto;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-tertiary);
  }
  /* The view toggle: the one icon-button treatment, nothing of its own. */
  .wc-view {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--text-tertiary);
    cursor: pointer;
    transition:
      color 0.2s ease,
      transform 0.2s ease;
  }
  .wc-view:hover {
    color: var(--blue-light);
    transform: scale(1.07);
  }
  .work-card.running .wc-state {
    color: var(--blue-light);
  }
  .work-card.error .wc-state {
    color: var(--red, #e5484d);
  }
  /* Capped and scrolling: a statechart being written is hundreds of lines, and a card that grows
     with it walks the rest of the conversation off the screen while the reader is watching it. */
  .wc-body {
    max-height: 320px;
    overflow: auto;
  }
  .wc-pre {
    margin: 0;
    padding: 10px 12px;
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 12px;
    line-height: 1.55;
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--text-secondary);
  }
  .x-tag {
    color: var(--blue-light);
  }
  .x-attr {
    color: var(--text-primary);
  }
  .x-val {
    color: var(--green, #46a758);
  }
  .x-comment {
    color: var(--text-tertiary);
    font-style: italic;
  }
  .x-text {
    color: var(--text-secondary);
  }

  .tool-row {
    display: flex;
    align-items: flex-start;
    /* Clear air between the truncated name and whatever the row's action is. With the head's own
       9px of right padding this is the padding-before-the-button the ellipsis needs to read as an
       ellipsis rather than as text that ran into a control. */
    gap: 6px;
  }
  .tool-row > .tool-head {
    flex: 1 1 auto;
    min-width: 0;
  }
  /*
   * THE ACTION SITS ON THE ROW'S FIRST LINE, not in its top-right corner.
   *
   * The row is flex-start — deliberately, so a wrapping message does not drag the chevron to the
   * middle of a two-line row — and the head carries its own 6px/9px padding. The action inherited
   * neither, so it was pinned to the very top edge with nothing between it and the border: higher
   * than the name it belongs to, and touching the corner. Given the head's padding, and centred on
   * the same first line the chevron uses, it lands level with the name whatever the message does.
   */
  .tool-row > *:not(.tool-head) {
    flex: none;
    display: inline-flex;
    align-items: center;
    box-sizing: content-box;
    /*
     * font: inherit FIRST, because the 1.45em below is meaningless without it.
     *
     * The action is a button element, and a button does not inherit font — the head next to it
     * says font: inherit and this did not, so it computed 1.45em against the browser's 13.33px
     * button default instead of the card's 12px. 19.3px of content where the head has 17.4px: the action
     * was 31.3px tall against the head's 29.4px, the row took the taller of the two, and because
     * the row is flex-start the extra 2px landed as dead space UNDER the name. Measured in the
     * running app, not deduced.
     */
    font: inherit;
    height: calc(1.45em);
    padding: 6px 9px 6px 0;
  }
  .tool-head {
    display: flex;
    /* Nothing leaves the head — see .tool-name. The action beside it is a sibling of the head, not
       a child, so anything overflowing here lands on top of it. */
    overflow: hidden;
    /*
     * EVERYTHING STARTS ON THE FIRST LINE.
     *
     * It was baseline, which aligns first baselines and is right until one item wraps — then the
     * chevron (centred) drifted to the middle of a two-line row and the name read as sitting above
     * the sentence beside it. flex-start plus one shared line-height makes the three of them start
     * at the same height whatever the message does.
     */
    align-items: flex-start;
    line-height: 1.45;
    gap: 7px;
    width: 100%;
    background: none;
    border: 0;
    padding: 6px 9px;
    margin: 0;
    font: inherit;
    color: var(--text-secondary);
    cursor: pointer;
    text-align: left;
  }
  /* The full message, once the row is open. Quiet, and above the payloads: it is prose about what
     happened, and the JSON below it is the evidence. */
  .tool-said {
    color: var(--text-secondary);
    font-size: 12px;
    line-height: 1.5;
    /* The body owns the spacing around itself now. A trailing margin here as well put a second gap
       under the last line of every row that has nothing below it — most of them. */
    margin: 0;
    white-space: pre-wrap;
  }
  /* Except where something follows it: the account is prose about what happened and the payload
     below is the evidence, and the two must not run together. */
  .tool-said:not(:last-child) {
    margin-bottom: 8px;
  }
  .tool-head:hover .tool-name {
    color: var(--ai);
  }
  /*
   * THE NAME TRUNCATES. EVERY ROW, EVERY TYPE, EVERY WIDTH.
   *
   * It was flex: none, which in a row that can be 300px wide is not "keep the name intact" — it is
   * "let the name run out of its own box". The head has no clip of its own, so the text painted
   * straight over the button on the row's right-hand end: the way into the agent, sitting under
   * the sentence naming it. Seen on a returned-subagent row, which has no message beside the name
   * to give way first, so the name is the whole line and the overrun is guaranteed.
   *
   * Shrinking with an ellipsis is the fix, and it belongs HERE rather than on any one row's
   * template because every row in a transcript — a call, a note from another agent, a return —
   * renders through this one class. The head's clip below is the backstop for anything inside it
   * that is not text; the row's own gap and the action's padding are what keep the ellipsis clear
   * of the button rather than against it.
   */
  .tool-name {
    color: var(--text-primary);
    font-weight: 500;
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: inherit;
  }
  /*
   * THE ONE-LINE ACCOUNT, AND ONE LINE IS WHAT IT GETS.
   *
   * It was allowed to wrap, and a tool whose result is a paragraph — the spawn verb returns four
   * sentences of instruction to the model — then printed that paragraph across the transcript,
   * inside a collapsed row, where its whole purpose is to be glanceable. It also dragged the
   * chevron and the name to the top of a four-line box, which is the "not vertically centred" that
   * the row was accused of and was really this.
   *
   * Clamped rather than dropped: the first clause of a tool's message is usually the useful part.
   * The whole of it is in the disclosure, and on the title.
   */
  .tool-msg {
    color: var(--text-secondary);
    min-width: 0;
    flex: 1;
    line-height: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /*
   * A CHEVRON, not a tick — the same disclosure the symbol tree uses, so a row that opens looks
   * like every other row in the product that opens. Rotating rather than swapping glyphs keeps it
   * one control in two positions.
   *
   * The state survives as COLOUR on the row: green for done is not information anybody needed
   * (a call that is finished is the normal case), but a FAILED call must still be obvious without
   * opening it, so the name goes red and the row's edge with it.
   */
  .tool-mark {
    display: inline-flex;
    align-items: center;
    /* The first line's height, so the glyph is centred ON that line rather than on the whole row. */
    height: calc(1.45em);
    width: 13px;
    color: var(--text-tertiary);
    transition: transform 0.15s ease;
  }
  .tool-head:hover .tool-mark {
    color: var(--ai);
  }
  .tool-head[aria-expanded='true'] .tool-mark {
    transform: rotate(90deg);
  }
  .tool.error {
    border-color: color-mix(in srgb, var(--red) 45%, var(--border-color));
  }
  .tool.error .tool-name,
  .tool.error .tool-mark {
    /* The full red, not the border's mix — a failed row's control read as pink, which is a colour
       this product does not otherwise use and which said nothing. */
    color: var(--red);
  }
  /* Neither failed nor confirmed: the outcome is genuinely not known. */
  .tool.unknown .tool-name {
    color: var(--text-tertiary);
  }
  /* Only a call still IN FLIGHT gets a spinner, and it replaces the chevron rather than joining it:
     there is nothing to disclose yet. */
  .tool.running .tool-mark {
    animation: tool-spin 1.4s linear infinite;
  }
  @keyframes tool-spin {
    to {
      transform: rotate(360deg);
    }
  }
  /*
   * OPENS AND CLOSES THE WAY EVERYTHING ELSE IN THIS APP DOES.
   *
   * The grid-rows pattern the tree navigator uses (0fr → 1fr with an overflow-hidden child), on the
   * symmetric easing the pill bar and composer use — so the motion is the SAME curve forwards and
   * backwards. An asymmetric ease is what makes a disclosure feel like it snaps shut after opening
   * gently, and it is the one thing people notice without being able to name.
   *
   * The body is always in the DOM; only the row track changes. Rendering it on open would give the
   * browser nothing to animate from, which is why a conditional disclosure can only ever pop.
   */
  .tool-fold {
    display: grid;
    grid-template-rows: 0fr;
    transition:
      grid-template-rows var(--fold-dur) var(--fold-ease),
      opacity var(--fold-dur) var(--fold-ease);
    opacity: 0;
  }
  .tool-fold.open {
    grid-template-rows: 1fr;
    opacity: 1;
  }
  /*
   * THE GRID ITEM CARRIES NOTHING WITH A HEIGHT OF ITS OWN, which is the whole reason the wrapper
   * inside it exists.
   *
   * grid-template-rows: 0fr collapses the TRACK, and min-height: 0 lets the item's CONTENT go
   * to nothing with it — but padding and a border are not content. They sit outside the content
   * box and are added back after the track has been sized, so a closed row still measured 18px:
   * 8px + 9px of padding and 1px of border-top, carried under every collapsed tool call in the
   * transcript. On a card whose visible row is 29px that is more than half again as tall as it
   * should be, and it reads exactly as it was described — the bottom going too low, the chevron and
   * the name sitting high in a box with dead space beneath them.
   *
   * box-sizing: border-box is the obvious fix and is the wrong one: it makes the closed height 0
   * by clamping the OPEN height to the padding too, so the disclosure reveals nothing. Both were
   * measured in the running app.
   *
   * So the item is bare and the wrapper below it carries the look. Nothing else changes: the border
   * and padding are still always in the DOM rather than appearing when the row opens, they are just
   * one level further in, where a zero-height track can clip them like everything else.
   */
  .tool-fold > .tool-body {
    overflow: hidden;
    min-height: 0;
  }
  .tool-inner {
    color: var(--text-secondary);
    line-height: 1.5;
    word-break: break-word;
    border-top: 1px solid var(--border-color);
    /*
     * INDENTED TO THE HEAD'S TEXT, not to the card's edge.
     *
     * The body had no padding at all, so what a row revealed started hard against the border while
     * the name above it started 29px in — the disclosure and the thing disclosed did not line up
     * with each other, and a message expanded into a slab of text that looked like it belonged to
     * the card rather than to the row. 9px of head padding + the 13px chevron + the 7px gap is
     * where the name begins, so that is where its content begins.
     */
    padding: 8px 9px 9px 29px;
  }
  /*
   * Nothing animates the payload's own height. The grid track clips it, and a second animation on
   * the inner scroller would be a second clock on the same motion — which is what the earlier
   * max-height hack was, and why the card looked like it was closing twice.
   */
  @media (prefers-reduced-motion: reduce) {
    .tool-fold {
      transition: none;
    }
  }
  /* A driver that fell over, said so. Without this the agents simply sit at queued forever,
     which reads as work that never started rather than work whose driver died. */
  .drive-err {
    color: var(--red);
    margin: 4px 0;
  }
  @media (prefers-reduced-motion: reduce) {
    .caret {
      animation: none;
    }
    .tool.running .tool-mark {
      animation: none;
    }
    /* No shimmer, so the label carries the liveness on its own. */
    .think-head.live .think-label {
      animation: none;
      background: none;
      -webkit-text-fill-color: currentColor;
      color: var(--ai);
    }
    .think-chevron {
      transition: none;
    }
  }
`;
/** Copy a fenced block's source. Silent on failure — a denied clipboard is not worth an alert. */
export function copyCode(code) {
    void navigator.clipboard?.writeText(code).catch(() => { });
}
export function renderTurn(turn, h = {}) {
    // A returned turn sits in the user slot because that is where the model reads it, but it is NOT
    // the user speaking — drawing it in the blue right-hand bubble would show them words they never
    // wrote. It gets its own quiet left-aligned treatment instead.
    if (turn.returned)
        return renderReturn(turn, h);
    // A peer's note is in the user slot for the same reason and lies in the same way. Same quiet
    // treatment, different label — a reader has to be able to tell "a colleague said this" from "the
    // job you were waiting on came back".
    if (turn.fromRunId)
        return renderNote(turn, h);
    /*
     * AND SO IS THE PLATFORM'S OWN VOICE — the third thing in the user slot that is not the user.
     *
     * The loop writes lines to the run mid-flight: the provenance challenge, the dangling-tool
     * warning, the rejected-answer notice. They go in the user slot because that is the only slot a
     * driver can speak to a model through, and they were drawn as the reader's own words — so a
     * person watching an agent saw themselves send a paragraph beginning "SYSTEM: before that answer
     * stands — 10 is reported against OB1, but 10 came back from hwa6uj7n…", in their own bubble, in
     * a thread where they had typed nothing.
     *
     * Recognised by the `SYSTEM: ` prefix, which is not a heuristic here: it is the marker the loop
     * itself routes on (`isOurs` in step.ts excludes these lines from the evidence a claim can be
     * sourced against, precisely because the platform saying a number does not make it real). One
     * marker, both readers.
     */
    if (turn.role === 'user' && isSystemLine(turn.text))
        return renderSystemLine(turn, h);
    const mine = turn.role === 'user';
    if (!mine)
        return renderSequence(turn, h);
    // prettier-ignore
    // The host's extras go in the person's bubble too — the console draws a message's attachments
    // through that hook, and a user row that skipped it showed the question with the file missing.
    return html `
    <div class="turn mine">
      <div class="bubble">${renderMarkdown(turn.text, copyCode, h)}${renderArtifacts(turn, h)}${h.extra?.(turn) ?? nothing}</div>
      ${renderActions(turn, h)}
    </div>
  `;
}
/**
 * WHAT THIS TURN PRODUCED — under the answer, because it is part of the answer.
 *
 * Below the prose rather than up among the tool rows, and that placement is the point. The tool
 * rows are the WORK: what was called, with what, and what came back. A chart is not work, it is
 * the deliverable — the thing the reader came for — and burying it inside a collapsed disclosure
 * would mean the assistant drew something and hid it behind a click.
 *
 * The bytes are fetched per tile, lazily, and only for the ones that are pictures. A transcript
 * that eagerly pulled every artifact it ever produced would re-download a session's worth of
 * charts on every reopen for the two the reader actually scrolls to.
 */
/**
 * A TURN AS THE SEQUENCE IT WAS.
 *
 * Each part is drawn where it happened: a thinking part as its own disclosure (open while it is
 * the live tail, folded once anything follows it), a tool part as the row for that call, a text
 * part as prose. The caret sits after the last part while the turn streams; the artifacts, the
 * provenance note and the action row keep their places at the end.
 */
function renderSequence(turn, h) {
    const parts = sequenceOf(turn);
    const seqKey = turn.seq ?? 'live';
    /*
     * POSITIONAL, NOT KEYED — on purpose. A sequence only ever grows at its end (a re-fold of the
     * archive reproduces the same prefix; the live path appends), so Lit's default reconciliation
     * reuses every element in place: a running call's spinner keeps turning across repaints and an
     * open disclosure stays open. The rows that used to jump were rows the OLD fold moved between
     * turns, and that fold is gone. A keyed directive would add a primitive to buy nothing.
     */
    const toolOf = (id) => turn.tools?.find((x) => x.id === id);
    const drawn = [];
    let lastTool = -1;
    for (let i = 0; i < parts.length; i++) {
        const p = parts[i];
        if (p.kind === 'reasoning') {
            drawn.push(renderThought(turn, h, p, `think:${seqKey}:${i}`));
            continue;
        }
        if (p.kind === 'text') {
            drawn.push(renderProse(p.text, h));
            continue;
        }
        const row = toolOf(p.id);
        if (!row)
            continue;
        /*
         * A RUN OF CALLS TO A CARD THAT LISTS THEM is one entry, not one per call — the questions a
         * model asked in one breath are walked as one list. Gathered here because only the sequence
         * knows what is consecutive; the card only knows how to draw what it is handed.
         */
        const card = workCardFor(row);
        if (card?.list) {
            const run = [row];
            for (let next = parts[i + 1]; next?.kind === 'tool'; next = parts[i + 1]) {
                const more = toolOf(next.id);
                if (!more || more.name !== row.name)
                    break;
                run.push(more);
                i++;
            }
            drawn.push(renderWorkList(run, card, workState));
        }
        else {
            drawn.push(renderTool(row, h));
        }
        lastTool = drawn.length - 1;
    }
    /*
     * THE PICTURE SITS WHERE IT WAS MADE. An artifact comes out of a tool, so it draws right after
     * the last call in the sequence — above any prose that follows and describes it — rather than at
     * the end, where streaming text would push the thing that just appeared away from where it
     * appeared. A turn with no calls (a picture on a resumed turn) shows it first.
     */
    const pictures = renderArtifacts(turn, h);
    const ordered = lastTool < 0 ? [pictures, ...drawn] : [...drawn.slice(0, lastTool + 1), pictures, ...drawn.slice(lastTool + 1)];
    const body = turn.error
        ? html `<div class="turn-err"><div class="turn-err-msg">${humanError(turn.error)}</div></div>`
        : html `${ordered}${turn.streaming ? html `<span class="caret">▍</span>` : turn.stopped ? html `<span class="stopped-note">${t('ai.stoppedHere')}</span>` : nothing}`;
    // prettier-ignore
    return html `
    <div class="turn">
      <div class="open">${body}${renderProvenance(turn)}${h.extra?.(turn) ?? nothing}</div>
      ${renderActions(turn, h)}
    </div>
  `;
}
/**
 * THE ONE SHAPE EVERY ASSISTANT TURN IS DRAWN FROM.
 *
 * A turn built by the fold or the live path carries its sequence. One built anywhere else — a
 * caller constructing `{ role, text }` by hand, a turn from before the sequence existed — carries
 * the three buckets, and they are normalised here into the only order they can mean: what it
 * thought, what it ran, what it said. One renderer, no second path to drift.
 */
export function sequenceOf(turn) {
    if (turn.parts?.length)
        return turn.parts;
    const out = [];
    if (turn.reasoning || turn.thinking)
        out.push({ kind: 'reasoning', text: turn.reasoning ?? '', ...(turn.reasoningMs ? { ms: turn.reasoningMs } : {}), live: !!turn.thinking });
    for (const t of turn.tools ?? [])
        out.push({ kind: 'tool', id: t.id });
    if (turn.text)
        out.push({ kind: 'text', text: turn.text });
    return out;
}
/*
 * ONE THOUGHT — A DISCLOSURE, LIKE A TOOL ROW.
 *
 * Collapsed by default: reasoning is long, repetitive and not the answer. Never hidden: it is the
 * only account of why the model did what it did, and a person watching an agent work — or
 * debugging why it looped — needs to open it, in the base thread and in every agent thread alike.
 * The same `toolOpen`/`onToggleTool` pair a tool row uses, keyed by the part's id, so a thought
 * folds with the same handlers and the same animation as everything else in the turn.
 */
function renderThought(turn, h, p, id) {
    const live = !!p.live && (!!turn.streaming || !!turn.thinking);
    const secs = Math.max(0.1, (p.ms ?? 0) / 1000);
    const label = live ? t('ai.thinking') : p.ms ? t('ai.thoughtFor', { seconds: secs < 10 ? secs.toFixed(1) : String(Math.round(secs)) }) : t('ai.thought');
    const text = p.text ?? '';
    // Nothing to open: a thought whose text was not kept is a label and only a label.
    if (!text) {
        return html `
      <div class="think">
        <div class="think-head sealed ${live ? 'live' : ''}"><span class="think-label">${label}</span></div>
      </div>
    `;
    }
    const open = (h.toolOpen?.(id) ?? false) || (live && !h.quiet);
    return html `
    <div class="think">
      <button class="think-head ${live ? 'live' : ''}" aria-expanded=${open ? 'true' : 'false'} @click=${() => h.onToggleTool?.(id)}>
        <span class="think-label">${label}</span>
        <span class="think-chevron"><vitesse-icon name="chevron-right" size="12"></vitesse-icon></span>
      </button>
      <div class="think-fold ${open ? 'open' : ''}">
        <div class="think-body">${renderMarkdown(text, copyCode)}</div>
      </div>
    </div>
  `;
}
/**
 * PROSE, AND NEVER THE LOOP'S OWN ENVELOPE.
 *
 * An agent's step ends in a machine protocol — the headline/done/summary/deliverable object — and
 * the archive keeps it verbatim, which is right for an auditor and wrong for a reader. A COMPLETE
 * envelope draws as its sentence (splitStepReply). An INCOMPLETE one — still streaming, or cut by
 * the archive's size cap, which is exactly what a deliverable carrying a whole statechart does —
 * parsed as nothing and fell through to raw markdown: a screen of JSON ending mid-string. That is
 * the "HEADLINE blob". It draws nothing: the headline is already in the header, and the sentence
 * arrives the moment the envelope completes.
 */
function renderProse(text, h) {
    const { narration, reply: step } = splitStepReply(text);
    if (step) {
        const said = step.summary || step.headline || '';
        return html `${narration ? renderMarkdown(narration, copyCode, h) : nothing}${said ? renderMarkdown(said, copyCode, h) : nothing}`;
    }
    if (looksLikeEnvelope(text))
        return nothing;
    return renderMarkdown(text, copyCode, h);
}
/**
 * A step envelope that is NOT FINISHED — still streaming, or cut by the archive's size cap.
 *
 * Recognised by its opening, which no prose begins with, AND by an unclosed brace: a complete
 * object of an unexpected shape is deliberately left alone (splitStepReply is strict, and a turn
 * that is not exactly the envelope keeps the rendering it always had — see step-envelope-render),
 * so only the case that can never be read by anyone is hidden.
 */
export function looksLikeEnvelope(text) {
    if (!/^\s*\{\s*"headline"\s*:/.test(text))
        return false;
    let depth = 0;
    let inString = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inString) {
            if (c === '\\')
                i++;
            else if (c === '"')
                inString = false;
            continue;
        }
        if (c === '"')
            inString = true;
        else if (c === '{')
            depth++;
        else if (c === '}')
            depth--;
    }
    return depth > 0 || inString;
}
function renderArtifacts(turn, h) {
    const files = turn.files ?? [];
    if (!files.length)
        return nothing;
    return html `<div class="artifacts">
    ${files.map((f) => html `<vitesse-artifact
        .filename=${f.filename}
        media-type=${f.mediaType}
        .size=${f.size}
        .sha256=${f.sha256}
        .load=${h.loadArtifact ? () => h.loadArtifact(f) : undefined}
      ></vitesse-artifact>`)}
  </div>`;
}
/**
 * WHAT THIS TURN SAID THAT ITS OWN TOOLS DO NOT SUPPORT.
 *
 * Inside the message, under the prose it qualifies, because it is about THIS answer — floated
 * anywhere else it becomes a notice the reader has to associate with something, which is exactly the
 * association that fails when someone is skimming.
 *
 * Only the two findings that state a fact: a link that appears in no result this turn received, and
 * a value that contradicts a call this turn made. Both are decidable, so both can be worded flatly.
 * The softer "no source in the record" check is deliberately absent from chat — conversational prose
 * is full of legitimate arithmetic, and a marker that fires on honest turns would be ignored within
 * a day, taking these two down with it.
 */
function renderProvenance(turn) {
    const wrong = turn.misattributed ?? [];
    const invented = turn.uncited ?? [];
    if (!wrong.length && !invented.length)
        return nothing;
    return html `<div class="provenance">
    ${wrong.length
        ? html `<div class="provenance-row">
            <vitesse-icon name="triangle-alert" size="13"></vitesse-icon
            ><span>${t('ai.turnWrongSubject', { items: wrong.join('; ') })}</span>
          </div>`
        : nothing}
    ${invented.length
        ? html `<div class="provenance-row">
            <vitesse-icon name="triangle-alert" size="13"></vitesse-icon
            ><span>${t('ai.turnNoSource', { items: invented.join(', ') })}</span>
          </div>`
        : nothing}
  </div>`;
}
/**
 * THE ACTION ROW — revealed on hover, on both speakers.
 *
 * On hover rather than always, because four controls under every message in a scrolling thread is
 * more chrome than conversation. They stay reachable without a pointer: the row is in the DOM at all
 * times and only its opacity changes, so it is tab-focusable and a focused control shows itself.
 *
 * BOTH SPEAKERS get the same four. Forking from your own question is the common case ("ask that
 * again but differently"), retrying an assistant turn is the other common case, and a chapter can
 * reasonably start at either. Offering them asymmetrically would mean explaining why.
 */
function renderActions(turn, h) {
    /*
     * NOTHING UNDER A TURN THAT IS STILL BEING TAKEN.
     *
     * Each individual action already refuses mid-stream — retry would race the answer it replaces,
     * speak would read half a sentence — but the ROW itself did not, so a turn in the middle of a
     * tool loop still drew its timestamp underneath. A finishing line under a turn that has not
     * finished is the strongest "this is over" signal the transcript has, which is exactly how a
     * working turn came to read as stopped-and-then-resumed.
     */
    if (turn.streaming || h.inFlight?.(turn))
        return nothing;
    const can = (fn, needsSeq = false) => !!fn && (!needsSeq || typeof turn.seq === 'number');
    const speakable = !!turn.text && !turn.streaming;
    // A retry replaces THIS answer, so it is offered on a finished assistant turn and nowhere else —
    // on a user message it would mean nothing, and mid-stream it would race the reply it is replacing.
    const retryable = !!h.onRetry && turn.role === 'assistant' && !turn.streaming && (!!turn.text || !!turn.error);
    const hasVersions = (h.versions?.(turn)?.count ?? 0) > 1;
    // A branch replaces a message and asks again, so it is offered where there is a message to
    // replace: the person's own turns. Branching an ANSWER is what Try again already does.
    const branchable = !!h.onBranch && turn.role === 'user' && typeof turn.seq === 'number' && !!turn.text;
    const stamp = h.timestamp?.(turn);
    if (!can(h.onFork, true) && !branchable && !retryable && !can(h.onPin, true) && !speakable && !hasVersions && !stamp)
        return nothing;
    const act = (icon, label, on, cls = '') => html `
    <button class="turn-act ${cls}" title=${label} aria-label=${label} @click=${on}>
      <vitesse-icon name=${icon} size="13"></vitesse-icon>
    </button>
  `;
    /*
     * The two self-contained actions re-render THEMSELVES rather than asking the host to.
     *
     * Copy-became-a-tick and read-became-stop are feedback about the button, not about the
     * conversation — routing them through the host would mean every surface that renders a turn has to
     * hold state it does not otherwise care about, and would repaint the whole transcript to swap one
     * glyph. Swapping the icon in place keeps it local and costs nothing.
     */
    const swap = (btn, icon, back, ms = 1400) => {
        const el = btn?.querySelector('vitesse-icon');
        if (!el)
            return;
        el.setAttribute('name', icon);
        if (back)
            setTimeout(() => el.setAttribute('name', back), ms);
    };
    const v = h.versions?.(turn) ?? null;
    return html `
    <div class="turn-acts">
      ${v && v.count > 1
        ? html `<span class="ver" title=${t('ai.branchVersion', { n: v.index, total: v.count })}>
              <button
                class="ver-arrow"
                ?disabled=${!v.prev}
                aria-label=${t('ai.branchPrev')}
                @click=${() => v.prev && h.onVersion?.(v.prev)}
              >
                <vitesse-icon name="chevron-left" size="12"></vitesse-icon></button
              ><span class="ver-n">${v.index}/${v.count}</span
              ><button
                class="ver-arrow"
                ?disabled=${!v.next}
                aria-label=${t('ai.branchNext')}
                @click=${() => v.next && h.onVersion?.(v.next)}
              >
                <vitesse-icon name="chevron-right" size="12"></vitesse-icon>
              </button>
            </span>`
        : nothing}
      ${branchable ? act('git-branch', t('ai.branchFromHere'), () => h.onBranch?.(turn), 'quarter') : nothing}
      ${can(h.onFork, true) ? act('git-fork', t('ai.forkCopy'), () => h.onFork?.(turn)) : nothing}
      ${retryable ? act('rotate-ccw', t('ai.retryTurn'), () => h.onRetry?.(turn)) : nothing}
      ${can(h.onPin, true) ? act('pin', t('ai.pinChapter'), () => h.onPin?.(turn)) : nothing}
      ${turn.text
        ? act('copy', t('ai.copyMessage'), (e) => {
            void navigator.clipboard?.writeText(turn.text);
            // The tick IS the confirmation — a toast for a copy is a notification about something
            // the person just did on purpose, in a corner they are not looking at.
            swap(e.currentTarget ?? null, 'check', 'copy');
        })
        : nothing}
      ${speakable
        ? act('volume-2', isSpeaking() ? t('ai.stopReading') : t('ai.readAloud'), (e) => {
            const btn = e.currentTarget;
            speak(turn.text, () => swap(btn, isSpeaking() ? 'square' : 'volume-2'));
        })
        : nothing}
      ${
    // A timestamp, when the host supplies one — a transcript does, the live panel does not. It
    // rides the action row so it appears with the same hover and sits on the same line as the
    // icons, ONE per message, rather than floating under it.
    stamp ? html `<span class="turn-stamp">${stamp}</span>` : nothing}
    </div>
  `;
}
/**
 * READ ALOUD, by the browser rather than by a provider.
 *
 * `speechSynthesis` is built into every target browser, costs nothing, needs no key and no round
 * trip — and, the part that actually decides it here, the text never leaves the machine. Sending a
 * transcript of what an operator asked about their plant to a speech vendor to save a download is a
 * confidentiality trade nobody asked for, on a convenience button.
 *
 * THE VOICE IS CHOSEN, not defaulted. The default is whatever the platform lists first, which on
 * macOS is a low-quality compact voice — the "terrible dictation voice". The good ones are already
 * installed and simply not picked: local, neural, and free. Preference order is quality first, then
 * a known-good name, then any local voice for the document's language, and only then whatever the
 * browser would have used anyway.
 */
let cachedVoice = null;
/** Names known to be the higher-quality local voices, best first. Names rather than a capability
 *  flag because the API exposes no quality signal at all — `localService` says where it runs, not
 *  how it sounds. An unknown platform simply falls through to the language match. */
const GOOD_VOICES = [
    'Samantha',
    'Ava',
    'Allison',
    'Serena',
    'Daniel',
    'Karen',
    'Moira',
    'Google US English',
    'Microsoft Aria',
    'Microsoft Jenny',
];
function pickVoice(synth) {
    if (cachedVoice)
        return cachedVoice;
    const all = synth.getVoices();
    if (!all.length)
        return null; // the list loads async on some browsers; the next press will have it
    const lang = (globalThis.document?.documentElement.lang || 'en').slice(0, 2).toLowerCase();
    const sameLang = all.filter((v) => v.lang.toLowerCase().startsWith(lang));
    const pool = sameLang.length ? sameLang : all;
    cachedVoice =
        pool.find((v) => GOOD_VOICES.some((n) => v.name.startsWith(n))) ??
            pool.find((v) => v.localService && !/compact/i.test(v.name)) ??
            pool[0] ??
            null;
    return cachedVoice;
}
/** Is something being read right now? Drives the button between "read" and "stop". */
export const isSpeaking = () => !!globalThis.speechSynthesis?.speaking;
/**
 * Start reading, or STOP if this is already being read.
 *
 * One control, two states, because a button that starts something with no way to stop it is the
 * complaint: a long answer reads for a minute and the only escape is closing the tab. Pressing it on
 * a DIFFERENT message cancels the first — pressing "read" on something means "read that one", and a
 * queue would leave the button feeling broken for as long as the first message took.
 */
function speak(text, onChange) {
    const synth = globalThis.speechSynthesis;
    if (!synth)
        return; // no engine — the control is simply not drawn
    if (synth.speaking) {
        synth.cancel();
        onChange();
        return;
    }
    const u = new SpeechSynthesisUtterance(text);
    const voice = pickVoice(synth);
    if (voice)
        u.voice = voice;
    u.rate = 1.02; // a hair above default: the stock rate reads as dictation rather than speech
    u.onend = onChange;
    u.onerror = onChange; // a cancel surfaces here; there is no `oncancel` on the utterance
    synth.speak(u);
    onChange();
}
/**
 * A FAILURE, SAID TO A PERSON.
 *
 * The transports speak in their own terms — "model call exceeded 120000ms" is a deadline, a unit and
 * a number, and it tells a reader nothing they can act on. Every failure on this platform explains
 * itself, so the few shapes that reach a transcript get a sentence with a next step in it.
 *
 * Anything unrecognised is passed through UNCHANGED rather than replaced by a generic apology: a
 * message written by whoever knew what went wrong beats one written here by someone who did not.
 */
export function humanError(raw) {
    /*
     * FALL BACK TO THE RAW MESSAGE, never to nothing.
     *
     * `t()` answers undefined before i18n has resources — at module-eval, in a unit test, and for a
     * beat during boot. Rendering that would put the string "undefined" where the explanation goes,
     * which is worse than the technical sentence this exists to improve on. So every lookup is
     * guarded: a translation when there is one, the original when there is not.
     */
    // The key type is the typed-dictionary union; `opts` is only ever the interpolation bag, so the
    // two call shapes are kept apart rather than widened into one overload the checker cannot match.
    const say = (out) => (typeof out === 'string' && out ? out : raw);
    // "went silent for", not "exceeded": the call may have been running happily for
    // many minutes. What failed is that the stream stopped, and telling somebody
    // their request "took too long" when it did not is a wrong explanation that
    // sends them to shorten a prompt that was never the problem.
    const m = /went silent for\s+(\d+)\s*ms/i.exec(raw);
    if (m)
        return say(t('ai.errStreamSilent', { seconds: Math.round(Number(m[1]) / 1000) }));
    if (/\b429\b|rate.?limit/i.test(raw))
        return say(t('ai.errRateLimited'));
    // The ONE thing a key failure actually is: this deployment holds no key for that provider
    // (llm.ts answers 503 "provider key unavailable"). Named exactly, because it is fixed by adding a
    // secret and by nothing else — least of all by an org admin looking at model governance.
    if (/provider key unavailable/i.test(raw))
        return say(t('ai.errNoProviderKey'));
    /*
     * A 401 SAYS AUTHENTICATION FAILED. IT DOES NOT SAY WHOSE.
     *
     * This branch used to answer "this account is not authorized for that model — an organization
     * admin can change which models are enabled", which is a specific diagnosis of a case the server
     * never reports: a model an org has disabled is FILTERED OUT of the offered list, so it cannot be
     * picked and cannot fail this way. Meanwhile every other 401 wore that sentence — a tool backend
     * refusing (`"symbols.search" could not be run: [unauthenticated]`), an expired session, a
     * provider rejecting a key. An admin reading it went looking at model permissions for a problem
     * that was a missing secret, which is the exact cost of guessing in a message.
     *
     * So it says what is known and keeps what it was told. This file's own header already required
     * that: "a message written by whoever knew what went wrong beats one written here by someone who
     * did not."
     */
    if (/\b(401|403)\b|unauthenticated|forbidden/i.test(raw))
        return say(t('ai.errAuth', { detail: raw }));
    // A 5xx STATUS, not any three digits that happen to start with a five: "500 rows" is prose, and
    // rewriting a sentence that was already clear is the failure mode of a catch-all.
    if (/\b5\d\d\b\s*(service\s+)?(unavailable|error|bad gateway|timeout)?/i.test(raw) &&
        /\b5\d\d\b/.test(raw) &&
        /(unavailable|error|gateway|upstream|provider|status)/i.test(raw))
        return say(t('ai.errUpstream'));
    return raw;
}
/**
 * WHICH AGENT A SPAWN ROW STARTED, in the row's own name.
 *
 * Every spawn read "Spawn Subagent" — derived correctly from the tool's identifier and useless in a
 * thread that spawned four of them, where the four rows were indistinguishable until each was
 * opened. The model already names the agent it is starting (that label is what the tree draws), so
 * the row says it: "Spawned scxml-author subagent".
 *
 * Narrow on purpose. One tool gets this treatment because one tool has a name inside its arguments
 * that a reader needs on the row; a general "put an argument in the title" rule would put a
 * different arbitrary field on every row in the transcript.
 *
 * Returns null for anything that is not a spawn, so the ordinary derivation stays the default.
 */
function spawnDisplayName(tool, h = {}) {
    if (!/(^|[._])spawn[_]?subagent$/i.test(tool.name))
        return null;
    let who = '';
    try {
        const a = JSON.parse(tool.argumentsJson || '{}');
        // `label` first: it is what the model wrote for a person to read, and what the tree card shows.
        // The expert id is the fallback — less friendly, still far better than nothing.
        who = String(a.label ?? a.expertId ?? a.expert_id ?? '').trim();
    }
    catch {
        /* Malformed arguments are still a spawn. The row keeps its generic name rather than vanishing. */
    }
    // Tense follows the AGENT, not the call: "Spawning…" from the moment the call goes out until the
    // agent it started has finished, "Spawned" after. The call's own state covers the first
    // milliseconds; the host's view of the run tree covers the minutes that follow.
    // THREE TENSES. "Spawning…" is the call itself — a second or two. Once the agent exists the row
    // says so, and says it is still working; "Spawning" for the ten minutes an agent takes read as
    // the spawn never having happened. When it returns, the row settles.
    if (tool.state === 'running')
        return who ? t('ai.spawningNamedSubagent', { name: who }) : t('ai.spawningSubagent');
    const working = h.agentRunning?.(spawnedRunIdOfTool(tool.data), rootRunIdOfTool(tool.data)) === true;
    if (working)
        return who ? t('ai.spawnedNamedWorking', { name: who }) : t('ai.spawnedWorking');
    return who ? t('ai.spawnedNamedSubagent', { name: who }) : t('ai.spawnedSubagent');
}
/**
 * THE TEAM VERBS, NAMED BY WHAT THEY DID rather than by what they are called.
 *
 * The same argument `spawnDisplayName` makes, for the family that grew around spawn. "Message Agent"
 * and "Read Agent Transcript" are correct derivations of the tool identifiers and they are the wrong
 * thing on a row: a thread where an agent messaged three peers shows three identical rows, and the
 * one fact a reader wants — WHICH peer — is inside each of them.
 *
 * So the row says who: "Messaged reader", "Read reader's notes". Discovery and broadcast address
 * nobody in particular and say so plainly, which is also the honest reading of what they did.
 *
 * Narrow on purpose, exactly as the spawn case is. These four have a NAME in their arguments that a
 * reader needs on the row; a general "hoist an argument into the title" rule would put a different
 * arbitrary field on every row in the transcript.
 *
 * Returns null for anything else, so the ordinary derivation stays the default.
 */
function teamDisplayName(tool) {
    const verb = /(^|[._])(discover_agents|message_agent|broadcast_to_agents|read_agent_transcript)$/i.exec(tool.name)?.[2]?.toLowerCase();
    if (!verb)
        return null;
    if (verb === 'discover_agents')
        return t('ai.teamListed');
    if (verb === 'broadcast_to_agents')
        return t('ai.broadcastSent');
    let who = '';
    try {
        const a = JSON.parse(tool.argumentsJson || '{}');
        // `to_run_id` is the message verb's field and `agent` the read verb's; both hold the NAME the
        // caller addressed, because that is the address these verbs take.
        who = String(a.agent ?? a.toRunId ?? a.to_run_id ?? '').trim();
    }
    catch {
        /* Malformed arguments are still the call it was. The row keeps a name rather than vanishing. */
    }
    if (!who)
        return null; // unaddressed — the derived name is more honest than a blank one
    return verb === 'message_agent' ? t('ai.messagedAgent', { name: who }) : t('ai.readNotes', { name: who });
}
/**
 * One tool call, as a row.
 *
 * COLLAPSED, like the thinking disclosure and for the same reason: the row answers "what did it
 * do" at a glance, and the arguments and result are there for whoever needs to check. The two use
 * one visual grammar deliberately — a turn can carry both, and inventing a second idiom for the
 * second kind of detail would make the transcript read as two designs stacked.
 *
 * The ARGUMENTS are shown when opened, not just the name. On a platform where a tool can write to
 * plant, "it called writeSymbol" without saying what it wrote is the half of the sentence that
 * does not matter.
 */
/** The one word beside a work card's title. Three states, because a call has three. */
function workState(tool) {
    if (tool.state === 'running')
        return t('ai.workWriting');
    return tool.state === 'error' ? t('ai.workFailed') : t('ai.workReady');
}
/** The reason a wait gave, from the arguments it was called with. '' when there is none to show. */
function waitReason(tool) {
    try {
        const args = JSON.parse(tool.argumentsJson || '{}');
        if (typeof args.reason !== 'string')
            return '';
        // The row already says "Waiting for:", so a reason the agent began with "Waiting …" would read
        // twice. Only that leading phrase is dropped; the rest is the agent's own words.
        const r = args.reason.trim().replace(/^(waiting|wait)\s+(for\s+)?/i, '').replace(/[.\s]+$/, '');
        return r ? r.charAt(0).toUpperCase() + r.slice(1) : '';
    }
    catch {
        return '';
    }
}
export function renderTool(tool, h = {}) {
    const open = h.toolOpen?.(tool.id) ?? false;
    // A chevron for anything that has settled — the row's job is to open. Only a call still in
    // flight shows something else, because there is nothing to disclose yet.
    const mark = tool.state === 'running' ? 'hammer' : 'chevron-right';
    const name = tool.name === 'ask_user'
        ? t('ai.askedYou')
        : isWaitTool(tool.name)
            ? t('ai.waiting')
            : (spawnDisplayName(tool, h) ?? teamDisplayName(tool) ?? toolDisplayName(tool.name));
    /*
     * A SPAWN SAYS NOTHING BESIDES ITS NAME, because what it would say is the same paragraph every
     * time.
     *
     * `SpawnSubagent` returns three sentences written FOR THE MODEL — "started, and has not run yet.
     * You do not have its result and must not state one…" — and they earn their place there: they are
     * what stops a model announcing an answer its sub-agent has not produced. On screen they are
     * constant, so every spawn row in every transcript carried the identical wall of instruction
     * addressed to somebody who is not reading it, next to a name that already said what happened.
     *
     * Hidden from the ROW, not from the reply: the model still receives the whole thing. The name
     * carries the meaning here — it names the job that started — and the arguments in the disclosure
     * carry the objective, which is the part a person actually wants.
     */
    const boilerplate = spawnDisplayName(tool) !== null;
    /*
     * A QUESTION PUT TO THE PERSON is the same row as everything else — its line is the question
     * (and the answer, once given), its fold the question, the options and what was said back. It
     * was a framed card of its own; one visual grammar for every call reads better than two.
     */
    const ask = tool.name === 'ask_user' ? askRow(tool) : undefined;
    /*
     * A WAIT SAYS WHAT IT IS WAITING FOR, while it waits.
     *
     * An open wait has no outcome yet, so the row would otherwise read "Waiting" and nothing else —
     * which is exactly the state a person needs to judge before pressing cancel. The reason the agent
     * gave is in its own arguments; once the wait ends, the wake or the cancel says what happened and
     * takes over the line like any other result.
     */
    const waitingFor = isWaitTool(tool.name) && !tool.message ? waitReason(tool) : '';
    const said = ask ? ask.said : boilerplate ? '' : waitingFor ? t('ai.waitingFor', { reason: waitingFor }) : tool.message;
    /*
     * A CARD, for the calls whose argument is the thing being made.
     *
     * A statechart the assistant is writing is the most substantial thing this product does and it
     * drew as the least: a one-line row named after a function, with the document folded away behind
     * a disclosure as escaped JSON, arriving whole when the call ended. Registered per tool by
     * whoever owns the domain — see work-card.ts for why it is a registry and not a switch.
     */
    const card = workCardFor(tool);
    if (card)
        return renderWorkCard(tool, card, workState, h);
    return html `
    <div class="tool ${tool.state}">
      <div class="tool-row">
        <button class="tool-head" aria-expanded=${open ? 'true' : 'false'} @click=${() => h.onToggleTool?.(tool.id)}>
          <span class="tool-mark"><vitesse-icon name=${mark} size="13"></vitesse-icon></span>
          <span class="tool-name" title=${tool.name}>${name}</span>
          ${said ? html `<span class="tool-msg" title=${said}>${said}</span>` : nothing}
        </button>
        ${h.toolAction?.(tool) ?? nothing}
      </div>
      <div class="tool-fold ${open ? 'open' : ''}">
        <div class="tool-body">
          <div class="tool-inner">
            <!-- WHOLE, here. The row clamps it to a line; this is where the rest of it lives, so
                 nothing a tool said is only ever half-shown. -->
            ${ask ? ask.body : said ? html `<div class="tool-said">${said}</div>` : nothing}
            ${renderJson(t('ai.toolArguments'), tool.argumentsJson, t('ai.noArguments'))}
            ${tool.data !== undefined ? renderJson(t('ai.toolResult'), JSON.stringify(tool.data)) : nothing}
            ${h.toolExtra?.(tool) ?? nothing}
          </div>
        </div>
      </div>
    </div>
  `;
}
/**
 * A MESSAGE FROM ANOTHER AGENT — a row, and the same row everything else in this exchange is.
 *
 * It used to be a `.ret` block with the message printed underneath it and a label reading "Note
 * from another agent". Two things were wrong with that and they are the same thing: it was a
 * shape nothing else in the transcript has, and it could not say WHO. The run id is a uuid, so the
 * sentence could not name the sender and the id sat on a tooltip where nobody looks.
 *
 * Now it is the tool row every other arrival uses — a spawn, a return, a tool call — collapsed,
 * with the message behind the disclosure. Three sides of one exchange drawn the same way: you
 * messaged an agent (a tool row), it received one (this), it came back (a return row). The name
 * comes from the run tree via `agentName`, because the transcript itself only carries the id.
 *
 * Keyed on the turn's own sequence: a message is a turn and has no call id, and two messages from
 * the same sender must open independently.
 */
export function renderNote(turn, h = {}) {
    const id = `msg:${turn.seq || turn.fromRunId || ''}`;
    const open = h.toolOpen?.(id) ?? false;
    const from = (turn.fromRunId && h.agentName?.(turn.fromRunId)) || '';
    /*
     * A COORDINATOR IS NOT "another agent", and there is nothing to name it with.
     *
     * The thread that spawned this agent can message it, and it sends as a chat TURN — an id with no
     * run row behind it, so `agentName` has nothing to return and every such row fell through to
     * "Note from another agent". That is the one relationship the reader already knows about, drawn
     * as the anonymous case: the agent whose objective this is, correcting it mid-flight.
     */
    const coordinator = (turn.fromRunId ?? '').startsWith(TURN_RUN_PREFIX);
    return html `
    <div class="tool ok">
      <div class="tool-row">
        <button class="tool-head" aria-expanded=${open ? 'true' : 'false'} @click=${() => h.onToggleTool?.(id)}>
          <span class="tool-mark"><vitesse-icon name="chevron-right" size="13"></vitesse-icon></span>
          <!-- Named when we can name it, and the id stays on the title either way: "not the user"
               is the load-bearing claim and it must survive a sender whose spawner named nothing. -->
          <span class="tool-name" title=${turn.fromRunId ?? ''}>
            ${from ? t('ai.messageFrom', { name: from }) : coordinator ? t('ai.messageFromSpawner') : t('ai.noteFrom')}
          </span>
          <span class="tool-msg" title=${turn.text}>${turn.text}</span>
        </button>
        ${h.returnAction?.(turn) ?? nothing}
      </div>
      <div class="tool-fold ${open ? 'open' : ''}">
        <div class="tool-body"><div class="tool-inner"><div class="tool-said">${turn.text}</div></div></div>
      </div>
    </div>
  `;
}
/**
 * A RETURN, AS A ROW — the same row its spawn was.
 *
 * WHAT THE READER IS NO LONGER ASKED TO DO. A finished agent used to unfold its whole answer into
 * the parent's transcript: the summary as a sentence in the row, then the provenance findings, then
 * the deliverable, then the sources, all expanded, in the middle of a conversation somebody was
 * reading. That is the child's report drawn as though it were the parent's answer, and it is not
 * one — the parent is about to READ it and say something, and that sentence is what was wanted.
 *
 * There are two honest ways to learn what an agent did, and neither is this row: wait for the
 * parent to summarise, or open the agent and read its own thread. So the row says that one came
 * back, and is the door to it. Nothing else.
 *
 * Drawn with the tool row's own classes rather than an imitation of them, so a spawn and its answer
 * are visibly two halves of one exchange. Opened, there is the return as JSON — the same treatment
 * every other payload in this transcript gets, instead of a bespoke stack of sections with a
 * warning banner across it.
 *
 * `toolOpen`/`onToggleTool` keyed on the RUN ID: a return is a turn and has no call id, and the run
 * it came from is the one stable name it has. (Param named `turn`, not `t`: `t` is the translation
 * function in this file, and a shadowed import compiles and then surprises whoever adds a label.)
 */
/**
 * The prefix the loop marks its own lines with.
 *
 * Shared with the driver rather than re-derived: `packages/agent-loop/src/step.ts` writes it and
 * excludes anything carrying it from the evidence a figure can be sourced against. A second
 * spelling here would put a line in the reader's bubble the moment either side edited its own.
 */
export const SYSTEM_LINE_PREFIX = 'SYSTEM: ';
export const isSystemLine = (text) => text.startsWith(SYSTEM_LINE_PREFIX);
/**
 * THE PLATFORM TALKING TO THE AGENT — a row, like everything else that is not a person speaking.
 *
 * Collapsed, because these are long and are addressed to the model rather than to the reader; the
 * whole line is behind the disclosure. The same shape as a spawn, a return and a peer note, which
 * is the point: four things arrive in a transcript without the user typing them, and a reader
 * should be able to tell all four from each other and from their own words at a glance.
 *
 * Keyed on the turn's sequence: a system line carries no call id, and two of them in one run must
 * open independently.
 */
export function renderSystemLine(turn, h = {}) {
    const body = turn.text.slice(SYSTEM_LINE_PREFIX.length).trim();
    const id = `sys:${turn.seq ?? body.slice(0, 24)}`;
    /*
     * NAMED BY WHAT HAPPENED. Every one of these read "System note", so a reader watching an agent
     * stop, be told to cite its sources, and start again saw a row that explained nothing — and then
     * an answer beginning "Understood, I've now made the attribution explicit" to a question nobody
     * on screen had asked. The prefixes are the loop's own (agent-loop step.ts), so the label is
     * derived from the line rather than sent beside it.
     */
    const label = /^before that answer stands/.test(body)
        ? t('ai.systemAskedSources')
        : /^that answer was rejected/.test(body)
            ? t('ai.systemRejected')
            : /^a previous attempt called/.test(body)
                ? t('ai.systemDangling')
                : t('ai.systemNote');
    const open = h.toolOpen?.(id) ?? false;
    return html `
    <div class="tool ok">
      <div class="tool-row">
        <button class="tool-head" aria-expanded=${open ? 'true' : 'false'} @click=${() => h.onToggleTool?.(id)}>
          <span class="tool-mark"><vitesse-icon name="chevron-right" size="13"></vitesse-icon></span>
          <span class="tool-name">${label}</span>
          <span class="tool-msg" title=${body}>${body}</span>
        </button>
      </div>
      <div class="tool-fold ${open ? 'open' : ''}">
        <div class="tool-body"><div class="tool-inner"><div class="tool-said">${body}</div></div></div>
      </div>
    </div>
  `;
}
export function renderReturn(turn, h = {}) {
    const r = parseReturn(turn.text);
    const id = `ret:${turn.fromRunId || turn.seq || r.head}`;
    const open = h.toolOpen?.(id) ?? false;
    /*
     * THE SAME SENTENCE AS ITS SPAWN, THE OTHER WAY ROUND.
     *
     * The spawn row says "Spawned reader subagent"; this one says "reader subagent returned", in the
     * agent's own name and not the raw head. Two halves of one exchange, told in one voice — which
     * only works if both use the label the model wrote rather than one using an internal string.
     *
     * The OUTCOME moves into the row's STATE rather than onto the row as words: a run that hit its
     * step limit is finished and is not done, and the tool rows already have a spelling for "this did
     * not go well". Reading it off the class is the same glance every other row in the transcript
     * gets, and it leaves the name uncluttered.
     */
    const failed = Boolean(r.outcome) && r.outcome !== 'completed';
    return html `
    <div class="tool ${failed ? 'err' : 'ok'}">
      <div class="tool-row">
        <button class="tool-head" aria-expanded=${open ? 'true' : 'false'} @click=${() => h.onToggleTool?.(id)}>
          <span class="tool-mark"><vitesse-icon name="chevron-right" size="13"></vitesse-icon></span>
          <span class="tool-name" title=${r.head}>${r.agent ? t('ai.subagentReturned', { name: r.agent }) : r.head}</span>
        </button>
        ${h.returnAction?.(turn) ?? nothing}
      </div>
      <div class="tool-fold ${open ? 'open' : ''}">
        <div class="tool-body"><div class="tool-inner">${renderJson(t('ai.returnPayload'), returnJson(r))}</div></div>
      </div>
    </div>
  `;
}
/**
 * The return, as ONE object.
 *
 * The sections `parseReturn` finds are folded back into a single payload rather than each getting a
 * strip of chrome of its own. Nothing is dropped — the provenance findings are keys like any other,
 * so a figure nobody measured is still named, in the place a reader goes to check what came back.
 * It is a fact about the answer and it reads as one here, instead of as an alarm laid across the
 * middle of somebody's conversation.
 *
 * The deliverable is re-parsed when it can be, so it nests as structure rather than sitting in the
 * payload as one long escaped string. `returnProse` truncates it at 4000 characters to bound the
 * parent's context, so what arrives is frequently not valid JSON; that case keeps the text.
 */
function returnJson(r) {
    const findings = {};
    for (const f of r.findings)
        findings[f.label] = f.text;
    let deliverable;
    if (r.deliverable) {
        try {
            deliverable = JSON.parse(r.deliverable);
        }
        catch {
            deliverable = r.deliverable;
        }
    }
    return JSON.stringify({
        agent: r.head,
        ...(r.summary ? { summary: r.summary } : {}),
        ...(Object.keys(findings).length ? { findings } : {}),
        ...(deliverable !== undefined ? { deliverable } : {}),
        ...(r.sources ? { sources: r.sources } : {}),
    });
}
//# sourceMappingURL=turn-view.js.map